import { useState } from "react";
import { Modal, Descriptions, Card, Input, Button } from "antd";
import { IPendingAdminUnlockUser } from "../hooks/useGetPendingAdminUnlockUsers";
import { useApproveAdminUnlock } from "../hooks/useApproveAdminUnlock";
import { useRejectAdminUnlock } from "../hooks/useRejectAdminUnlock";

interface IUnlockRequestModalProps {
  user: IPendingAdminUnlockUser;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UnlockRequestModal({
  user,
  onClose,
  onSuccess,
}: IUnlockRequestModalProps) {
  const [approvalComment, setApprovalComment] = useState("");
  const [actionType, setActionType] = useState<"approve" | "reject" | null>(
    null,
  );

  const { mutate: approveMutate, result: approveResult } =
    useApproveAdminUnlock();
  const { mutate: rejectMutate, result: rejectResult } = useRejectAdminUnlock();
  const isSubmitting = approveResult.isLoading || rejectResult.isLoading;

  const handleAction = (type: "approve" | "reject") => {
    const payload = {
      transactionReferenceNumber: user.transactionReferenceNumber,
      approvalComment,
      ssoUserId: user.sssoUserId,
    };
    const mutate = type === "approve" ? approveMutate : rejectMutate;
    mutate(payload, { onSuccess });
  };

  return (
    <Modal
      title={<h2 className="text-2xl font-semibold">Unlock Request Details</h2>}
      open
      onCancel={onClose}
      footer={null}
      width={700}
      bodyStyle={{ maxHeight: "80vh", overflow: "auto" }}
    >
      <div className="space-y-6 p-4">
        <Card title="User Information" className="shadow-sm">
          <Descriptions column={2}>
            <Descriptions.Item label="Name">
              {user.lockedUserName}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {user.lockedUserMail}
            </Descriptions.Item>
            <Descriptions.Item label="Workflow Code">
              {user.workflowCode}
            </Descriptions.Item>
            <Descriptions.Item label="Reference">
              {user.transactionReferenceNumber}
            </Descriptions.Item>
          </Descriptions>
        </Card>
        <br />
        <Card title="Lock / Unlock Reasons" className="shadow-sm">
          <Descriptions column={1}>
            <Descriptions.Item label="Lock Reason">
              {user.lockReason}
            </Descriptions.Item>
            {user.additionalLockDescription && (
              <Descriptions.Item label="Additional Lock Info">
                {user.additionalLockDescription}
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Requested Unlock Reason">
              {user.unlockReason}
            </Descriptions.Item>
            {user.additionalUnlockDescription && (
              <Descriptions.Item label="Additional Unlock Info">
                {user.additionalUnlockDescription}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
        <br />
        <div>
          <label className="label-txt">Comment</label>
          <Input.TextArea
            rows={4}
            placeholder="Add a comment..."
            value={approvalComment}
            onChange={(e) => setApprovalComment(e.target.value)}
            style={{ resize: "vertical" }}
          />
        </div>
        <br />
        <div className="d-flex justify-content-end" style={{ gap: "8px" }}>
          <Button
            size="large"
            disabled={rejectResult.isLoading}
            loading={actionType === "reject" && rejectResult.isLoading}
            onClick={() => {
              setActionType("reject");
              handleAction("reject");
            }}
          >
            Decline
          </Button>
          <Button
            type="primary"
            size="large"
            disabled={approveResult.isLoading}
            loading={actionType === "approve" && approveResult.isLoading}
            onClick={() => {
              setActionType("approve");
              handleAction("approve");
            }}
          >
            Approve
          </Button>
        </div>
      </div>
    </Modal>
  );
}
